import { useEffect, useRef } from 'react';
import { imageUrl } from '../lib/images';

export default function Lightbox({ images = [], currentIndex = 0, isOpen = false, onClose, onSelectIndex }) {
  const touchStartX = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight' && images.length > 1) {
        onSelectIndex?.((currentIndex + 1) % images.length);
      }
      if (e.key === 'ArrowLeft' && images.length > 1) {
        onSelectIndex?.((currentIndex - 1 + images.length) % images.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, currentIndex, images.length, onClose, onSelectIndex]);

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (diff > 50 && images.length > 1) {
      // swipe right -> prev
      onSelectIndex?.((currentIndex - 1 + images.length) % images.length);
    } else if (diff < -50 && images.length > 1) {
      // swipe left -> next
      onSelectIndex?.((currentIndex + 1) % images.length);
    }
    touchStartX.current = null;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedgalleri forstørrelse"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md transition-opacity animate-in fade-in duration-200"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top bar with count & close button */}
      <div className="absolute top-0 inset-x-0 p-4 sm:p-6 flex items-center justify-between text-white z-10" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-medium tracking-wide bg-black/40 px-3 py-1.5 rounded-full border border-white/10 backdrop-blur-xs">
          {currentIndex + 1} / {images.length}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
          aria-label="Luk galleri"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
        </button>
      </div>

      {/* Prev button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelectIndex?.((currentIndex - 1 + images.length) % images.length);
          }}
          className="absolute left-3 sm:left-6 z-10 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-lg sm:text-xl" aria-hidden="true" />
        </button>
      )}

      {/* Image container */}
      <div
        className="max-w-5xl max-h-[85vh] p-4 flex items-center justify-center select-none"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageUrl(currentImage, 'width=1600,quality=90,format=auto')}
          alt={`Galleri billede ${currentIndex + 1}`}
          className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-150"
        />
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelectIndex?.((currentIndex + 1) % images.length);
          }}
          className="absolute right-3 sm:right-6 z-10 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-lg sm:text-xl" aria-hidden="true" />
        </button>
      )}

      {/* Bottom thumbnails on desktop */}
      {images.length > 1 && (
        <div
          className="hidden md:flex absolute bottom-4 inset-x-0 justify-center gap-2 px-4 overflow-x-auto py-2 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onSelectIndex?.(idx)}
              className={`relative w-16 h-12 rounded-md overflow-hidden transition-all shrink-0 border-2 ${
                idx === currentIndex ? 'border-[#C9A770] scale-105 opacity-100' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img
                src={imageUrl(img, 'width=160,height=120,fit=cover,format=auto')}
                alt={`Miniature ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
